/**
 * Cleo ERP SaaS — Configuration billing admin
 */
import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Form, Input, InputNumber, Select,
  Button, Space, Alert, Spin, message, Divider, Tag,
} from 'antd';
import { LoadingOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../../../api/axios';

const { Title, Text } = Typography;

function BillingConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();

  const fetchConfig = async () => {
    try {
      const res = await api.get('/api/admin/billing/config/');
      setConfig(res.data);
      form.setFieldsValue({
        paddle_environment: res.data.paddle_environment,
        grace_period_days: res.data.grace_period_days,
        suspension_warning_offset_days: res.data.suspension_warning_offset_days,
        internal_alert_offset_days: res.data.internal_alert_offset_days,
      });
    } catch {
      setError('Impossible de charger la configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await api.patch('/api/admin/billing/config/', values);
      message.success('Configuration mise à jour.');
      fetchConfig();
    } catch (err) {
      if (err.response) {
        message.error('Erreur lors de la sauvegarde.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 36, color: '#10B981' }} />} />
    </div>
  );

  if (error) return <Alert message={error} type="error" showIcon />;

  return (
    <div style={{ maxWidth: 680 }}>
      <Title level={4} style={{ color: '#0F172A', marginBottom: 24 }}>Configuration Billing</Title>

      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 24 }}>
        <Title level={5} style={{ color: '#0F172A', marginBottom: 16 }}>Environnement Paddle</Title>
        <Form form={form} layout="vertical">
          <Form.Item name="paddle_environment" label="Environnement">
            <Select
              options={[
                { value: 'production', label: 'Production' },
                { value: 'sandbox', label: 'Sandbox (tests)' },
              ]}
              style={{ width: 220 }}
            />
          </Form.Item>
        </Form>
        {config?.paddle_environment === 'sandbox' && (
          <Alert
            type="warning"
            showIcon
            message="Mode sandbox actif — les paiements sont simulés."
            style={{ borderRadius: 8 }}
          />
        )}
      </Card>

      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 24 }}>
        <Title level={5} style={{ color: '#0F172A', marginBottom: 16 }}>Relances et suspensions</Title>
        <Form form={form} layout="vertical">
          <Form.Item
            name="grace_period_days"
            label="Période de grâce avant suspension (jours)"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={60} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item
            name="suspension_warning_offset_days"
            label="Avertissement avant suspension (jours avant la fin de grâce)"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={30} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item
            name="internal_alert_offset_days"
            label="Alerte interne EC Intelligence (jours après premier échec)"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={30} style={{ width: 120 }} />
          </Form.Item>
        </Form>
      </Card>

      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 24 }}>
        <Title level={5} style={{ color: '#0F172A', marginBottom: 8 }}>Destinataires alertes internes</Title>
        <Text style={{ color: '#64748B', fontSize: 13 }}>
          Configurables uniquement via l'interface Django admin (/django-admin/).
        </Text>
        <div style={{ marginTop: 12 }}>
          {(config?.internal_notification_recipients || []).map((email) => (
            <Tag key={email} style={{ borderRadius: 6, marginBottom: 4 }}>{email}</Tag>
          ))}
        </div>
      </Card>

      <Divider />

      <Space>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          style={{ background: '#10B981', borderColor: '#10B981', borderRadius: 8 }}
        >
          Enregistrer
        </Button>
      </Space>

      {config?.updated_at && (
        <div style={{ marginTop: 16 }}>
          <Text style={{ color: '#94A3B8', fontSize: 12 }}>
            Dernière modification : {new Date(config.updated_at).toLocaleString('fr-FR')}
          </Text>
        </div>
      )}
    </div>
  );
}

export default BillingConfigPage;
